import classnames from 'classnames'
import classNames from 'classnames'
import { groupBy } from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format, isAfter, startOfMonth, subMonths } from 'date-fns'
import { Link } from 'react-router-dom'
import GroupAvatar from '../../components/GroupAvatar/GroupAvatar'

import LearningItemAvatar from '../../components/LearningItemAvatar/LearningItemAvatar'
import StartCourseLink from '../../components/StartCourseLink/StartCourseLink'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import scheduleItems from '../../state/calculations/scheduleItems'
import { useGroups, useUser, useUsers } from '../../state/entities'
import useRoutes from '../../state/useRoutes'
import useUserLearningItems from '../../state/queries/useUserLearningItems'

import styles from './DashboardPage.module.scss'

export default React.memo(function DashboardPageSchedule({ userUuid }: { userUuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()

  const { user } = useUser(userUuid)

  const { userLearningItems, isPlaceholderData } = useUserLearningItems(userUuid)

  const items = useMemo(() => {
    const startOfLastMonth = startOfMonth(subMonths(new Date(), 1))
    return scheduleItems({ learningItems: userLearningItems, t, currentUserUuid: userUuid }).filter((item) =>
      isAfter(item.date, startOfLastMonth)
    )
  }, [userLearningItems, t])

  const { users } = useUsers(items.map((item) => item.assignments[0]?.assignedByUserUuid))
  const { groups } = useGroups(items.map((item) => item.assignments[0]?.assignedByGroupUuid))

  const monthGroups = useMemo(
    () =>
      Object.values(
        groupBy(
          items.map((item, idx) => ({ ...item, assigningUser: users[idx], assigningGroup: groups[idx] })),
          (item) => format(item.date, 'yyyy.MM')
        )
      ),
    [items, users, groups]
  )

  return (
    <section className={classnames(styles.DashboardPageSchedule, { skeleton: isPlaceholderData })}>
      <header>
        <h1>{t('dashboard.title my schedule')}</h1>
      </header>

      <main>
        <table>
          {monthGroups.map((group, groupIdx) => (
            <tbody key={groupIdx}>
              <tr className={styles.monthGroupHeader}>
                <th scope="rowgroup" colSpan={4}>
                  {t('format.MONTHYEAR', { date: group[0].date })}
                </th>
                <th scope="col">{t('learning item.assigned by')}</th>
                <th></th>
              </tr>
              {group.map((item, idx) => (
                <tr key={idx} className={classNames({ [styles.bookable]: item.type === 'bookable event' })}>
                  <td className={styles.dateColumn}>
                    <span>{t('format.DAYMONTH', { date: item.date })}</span>
                  </td>
                  <td className={styles.timeColumn}>
                    {item.type === 'due'
                      ? t('learning item.due date')
                      : item.endDate
                      ? t('format.TIMERANGE', { range: { start: item.date, end: item.endDate } })
                      : t('format.TIME', { date: item.date })}
                  </td>
                  <td className={styles.avatarColumn}>
                    <LearningItemAvatar learningItem={item.learningItem} />
                  </td>
                  <td className={styles.infoColumn}>
                    <Link to={routes.course.url({ uuid: item.learningItem.uuid })}>{item.learningItem.title}</Link>
                    <span>
                      {(item.info || []).filter(Boolean).map((i: any, idx: number) => (
                        <span key={idx}>{i}</span>
                      ))}
                    </span>
                  </td>
                  <td className={styles.assignedByColumn}>
                    {item.assignments[0]?.assignedByUserUuid && <UserAvatar link user={item.assigningUser} />}
                    {item.assignments[0]?.assignedByGroupUuid && <GroupAvatar link group={item.assigningGroup} />}
                    {item.type !== 'trainer event' &&
                      !item.assignments[0]?.assignedByUserUuid &&
                      !item.assignments[0]?.assignedByGroupUuid && <UserAvatar link user={user} />}
                  </td>
                  <td className={styles.actionColumn}>
                    <StartCourseLink small filled learningItem={item.learningItem} fallbackToCoursePage />
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
          {monthGroups.length === 0 && (
            <tbody>
              <tr>
                <td className={styles.empty}>{t('dashboard.schedule empty')}</td>
              </tr>
            </tbody>
          )}
        </table>
      </main>
    </section>
  )
})
